import { useState } from "react";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { TEAM } from "@/lib/data";
import LeadershipStrip from "@/components/LeadershipStrip";
import TeamMemberModal from "@/components/TeamMemberModal";
import TeamPhoto from "@/components/TeamPhoto";
import Seo from "@/components/Seo";

export default function Team() {
    const [active, setActive] = useState(null);

    return (
        <div data-testid="team-page">
            <Seo
                path="/team"
                title="Team — Founder-Led, Partner-Reviewed Engagements"
                description="Meet the senior people behind every Vittaniti engagement: CA Gopichand A (Founder & Partner, ex-PwC/KPMG) and Pranoy (Remote Team Lead, Big-4 Europe & UK experience)."
                keywords="Vittaniti team, CA Gopichand, offshore CFO India, Big-4 audit team India, founder-led bookkeeping firm"
            />
            <LeadershipStrip onSelect={setActive} />

            <section className="container-page pt-16 md:pt-24 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">The Vittaniti team</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-ink max-w-3xl">
                    The faces behind <span className="italic text-brand-amber">your</span> ledger<span className="brand-dot">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-brand-ink/70 leading-relaxed">
                    A small, senior bench. Every Vittaniti client is paired with a partner who knows their
                    business, their numbers, and their next milestone.
                </p>
            </section>

            <section className="section container-page pt-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl">
                {TEAM.map((m, i) => {
                    return (
                        <article
                            key={m.slug}
                            data-testid={`team-card-${m.slug}`}
                            className="card-3d overflow-hidden group animate-fade-up"
                            style={{ animationDelay: `${i * 70}ms` }}
                        >
                            <div className="aspect-[4/5] overflow-hidden bg-brand-creamLight">
                                <TeamPhoto
                                    member={m}
                                    className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                                <div className="p-7">
                                    <h3 className="font-serif text-2xl text-brand-ink leading-tight">{m.name}</h3>
                                    <p className="overline text-brand-amber mt-1">{m.role}</p>
                                    <p className="text-sm text-brand-muted mt-4 leading-relaxed">{m.bio}</p>
                                    <div className="mt-5 flex items-center justify-between">
                                        <button
                                            type="button"
                                            onClick={() => setActive(m)}
                                            data-testid={`team-read-bio-${m.slug}`}
                                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink link-underline"
                                        >
                                            Read full bio <ArrowUpRight size={14} />
                                        </button>
                                        <a
                                            href={m.linkedin}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={`${m.name} on LinkedIn`}
                                            data-testid={`team-linkedin-${m.slug}`}
                                            className="grid place-items-center w-9 h-9 rounded-full border border-brand-border text-brand-ink hover:bg-brand-creamLight transition-colors"
                                        >
                                            <Linkedin size={14} className="text-brand-amber" />
                                        </a>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <TeamMemberModal member={active} open={!!active} onOpenChange={(v) => !v && setActive(null)} />
        </div>
    );
}
